import { EndPoints } from '../../axios/endPoints';
import { Button, Divider, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DatasetIcon from '@mui/icons-material/Dataset';
import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { enqueueSnackbar } from '../../store/app-slice/app';
import { handleNotificationVariant } from '../notifications/_common';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { ArrowCircleLeftTwoTone, ArrowCircleRightTwoTone, KeyboardDoubleArrowLeftOutlined, KeyboardDoubleArrowRightOutlined } from '@mui/icons-material';
import SelectInput from '../use-form-components/SelectInput';
import Transition from '../common/Transition';
import { AIHealthCheck, AIPredict, GetTestingDatasetDetails, handleMoveAllColumnsToColumns, handleMoveAllColumnsToInput, handleMoveColumnToColumns, handleMoveColumnToInput, handleNewPrediction, handleSelectColumn, handleSelectInputColumn } from '../../store/mltester-slice/mltester';
import { Header } from '../common/Header';
import RedirectIconButton from '../common/RedirectIconButton';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';

const schema = (t : Function) => yup.object().shape({
    name: yup.string().required(t('NEW_PREDICTION_NAME_REQUIRED')),
    model: yup.object().required(t('NEW_PREDICTION_TYPE_REQUIRED')),
}).required();

const NewPredictionDialog = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { 
        columns,
        inputColumns, 
        selectedColumn, 
        selectedInputColumn,
        newPredictionDialogOpen, 
        newPredictionLoading, 
        selectedTesterRow,
        performingAIHealthCheck,
        fileDetailsLoading
    } = useAppSelector((state: RootState) => state.mltester);
    const { control, getValues, setValue, handleSubmit, formState: { errors } } = useForm({  
        mode: "onBlur", 
        resolver: yupResolver(schema(t)) 
    });

    function submitForm(data: any) {
        if(inputColumns.length == 0) {
            dispatch(enqueueSnackbar({ message: t('INPUT_COLUMNS_VALIDATION'), options: { variant: 'error', persist: true } }));
            return;
        }

        dispatch(AIHealthCheck())
            .then((response : any) => {
                if(response && response.payload && response.payload.success) {
                    var modelTypeId : any = getValues('model');
                    dispatch(AIPredict({
                        mlTestingDatasetId: selectedTesterRow.id,
                        mlResultName: getValues('name'),
                        mlTrainedModelId: modelTypeId ? modelTypeId.id : null,
                        inputColumns: inputColumns.map((el : any) => el.name),
                    })).then((res : any) => {
                        if(res && res.payload) { 
                            dispatch(enqueueSnackbar({ 
                                message: t(`${res.payload.message}`), 
                                options: { 
                                    variant: handleNotificationVariant(res.payload.success),
                                    persist: !res.payload.success
                                } 
                            }))
                        }
                    })
                }
                else dispatch(enqueueSnackbar({ message: t('AI_HEALTH_CHECK_ERROR'), options: { variant: 'error', persist: true } }));
            })
            .catch((e : any) => {
                dispatch(enqueueSnackbar({ message: t('AI_HEALTH_CHECK_ERROR'), options: { variant: 'error', persist: true } }))
            })
    }

    function handleClose() {
        dispatch(handleNewPrediction({ isOpen: false }))
        setValue('name', '');
        setValue('model', {});
    }

    function checkPredictionStatus() {
        if(newPredictionLoading || performingAIHealthCheck) {
            return true;
        }
        else return false;
    }

    useEffect(() => {
        if(newPredictionDialogOpen)
            dispatch(GetTestingDatasetDetails(selectedTesterRow.id));

        return () => {
            setValue('name', '');
            setValue('model', {});
        }
    }, [newPredictionDialogOpen])

    return(
        <React.Fragment>
            <Dialog
                open={newPredictionDialogOpen}
                TransitionComponent={Transition}
                keepMounted
                onClose={(_, reason) => {
                    if(reason !== 'backdropClick' && !checkPredictionStatus()) handleClose();
                }}
                aria-describedby="alert-dialog-slide-description"
                fullWidth
                maxWidth="md"
            >
                <form onSubmit={handleSubmit(submitForm)}>
                    <Header 
                        title={t('NEW_PREDICTION_DIALOG_TITLE')} 
                        helperIcon={
                            <RedirectIconButton to={'/help/6'} className="helper-icon" >
                                <ContactSupportIcon />
                            </RedirectIconButton>
                        }
                    />
                    <DialogContent sx={checkPredictionStatus() ? { pointerEvents: 'none'} : {}}>
                        <Grid container display={"flex"} flexDirection={"column"} spacing={2}>
                            <Grid item style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <DatasetIcon style={{ fontSize: '20px', color: 'darkgray' }}/>
                                <Typography ml={1} variant='body1'>{t('NEW_PREDICTION_DATASET_LABEL', { name: selectedTesterRow.name })}</Typography>
                            </Grid>
                            <Grid item><Divider /></Grid>
                            <Grid item>
                                <Controller control={control} name="name" render={({field, fieldState: { error }}) => 
                                    ( 
                                        <TextField 
                                            {...field} 
                                            fullWidth 
                                            variant="outlined" 
                                            label={t('NEW_PREDICTION_NAME_FIELD')} 
                                            type="text"
                                            error={error !== undefined}
                                            helperText={error ? errors.name?.message : ''}
                                            size="small"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item>
                                <SelectInput
                                    url={EndPoints.ML_TESTER_GET_TRAINED_MODEL}
                                    name={'model'}
                                    label={t('NEW_PREDICTION_MODEL_FIELD')}
                                    idName={'id'}
                                    labelName={'description'}
                                    multiple={false}
                                    disabled={false}
                                    control={control}
                                    translateName={false}
                                />
                            </Grid>
                            <Grid item><Divider /></Grid>
                            { fileDetailsLoading ? 
                            <Grid item p={2}>
                                <Box sx={{ width: '100%' }}>
                                    <LinearProgress />
                                </Box>
                            </Grid>
                            : 
                            <Grid item>
                                <Grid
                                    container
                                    display="flex"
                                    spacing={2}
                                    direction={{ xs: 'column', md: 'row' }}
                                    sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}
                                >
                                    <Grid item xs={12} md={5}>
                                        <Grid container display="flex" flexDirection="column" spacing={1} sx={{ minWidth: 0 }}>
                                            <Grid item>
                                                <Typography variant="button">{t('NEW_PREDICTION_INPUT_COLUMNS_LABEL')}</Typography>
                                            </Grid>
                                            <Grid id="new-prediction-dialog-border" item display="flex" flexDirection={"column"} sx={{ borderRadius: '5px', height: '200px', overflowY: 'auto' }}>
                                                <Grid container>
                                                {
                                                    inputColumns?.map((el : { id: number, name: string, type: string }, index: number) => {
                                                        return(
                                                            <Grid 
                                                                key={index}
                                                                xs={12}
                                                                item 
                                                                style={selectedInputColumn?.id == el.id ? { backgroundColor: 'gray', color: 'white' } : {}} 
                                                                sx={{ cursor: 'pointer' }} 
                                                                onClick={() => dispatch(handleSelectInputColumn(el))}
                                                            >
                                                                <Typography
                                                                    ml={1}
                                                                    sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                                                >
                                                                    { `• ${el.name} / ${t(el.type)}` }
                                                                </Typography>
                                                            </Grid>
                                                        )
                                                    })
                                                }
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        md={2}
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        sx={{ mt: { xs: 0, md: 3 } }}
                                    >
                                        <Grid
                                            container
                                            display="flex"
                                            flexDirection={{ xs: 'row', md: 'column' }}
                                            justifyContent="center"
                                            alignItems="center"
                                            spacing={1}
                                            sx={{ width: 'auto' }}
                                        >
                                            <Grid item>
                                                <Tooltip title={t('NEW_PREDICTION_MOVE_ALL_TO_RIGHT')}>
                                                    <IconButton onClick={() => {
                                                        dispatch(handleMoveAllColumnsToColumns());
                                                    }}>
                                                        <KeyboardDoubleArrowRightOutlined id="new-prediction-dialog-button" sx={{ fontSize: '30px' }}/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item>
                                                <Tooltip title={t('NEW_PREDICTION_MOVE_TO_RIGHT')}>
                                                    <IconButton onClick={() => {
                                                        if(selectedInputColumn) dispatch(handleMoveColumnToColumns(selectedInputColumn))
                                                    }}>
                                                        <ArrowCircleRightTwoTone id="new-prediction-dialog-button" sx={{ fontSize: '30px' }}/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item>
                                                <Tooltip title={t('NEW_PREDICTION_MOVE_TO_LEFT')}>
                                                    <IconButton onClick={() => {
                                                        if(selectedColumn) dispatch(handleMoveColumnToInput(selectedColumn))
                                                    }}>
                                                        <ArrowCircleLeftTwoTone id="new-prediction-dialog-button" sx={{ fontSize: '30px' }}/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item>
                                                <Tooltip title={t('NEW_PREDICTION_MOVE_ALL_TO_LEFT')}>
                                                    <IconButton onClick={() => {
                                                        dispatch(handleMoveAllColumnsToInput());
                                                    }}>
                                                        <KeyboardDoubleArrowLeftOutlined id="new-prediction-dialog-button" sx={{ fontSize: '30px' }}/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <Grid container display="flex" flexDirection="column" spacing={1} sx={{ minWidth: 0 }}>
                                            <Grid item>
                                                <Typography variant="button">{t('NEW_PREDICTION_COLUMNS_LABEL')}</Typography>
                                            </Grid>
                                            <Grid id="new-prediction-dialog-border" item display="flex" flexDirection={"column"} sx={{ borderRadius: '5px', height: '200px', overflowY: 'auto' }}>
                                                <Grid container>
                                                {
                                                    columns?.map((el : { id: number, name: string, type: string }, index: number) => {
                                                        return(
                                                            <Grid 
                                                                key={index}
                                                                xs={12}
                                                                item 
                                                                style={selectedColumn?.id == el.id ? { backgroundColor: 'gray', color: 'white' } : {}} 
                                                                sx={{ cursor: 'pointer' }} 
                                                                onClick={() => dispatch(handleSelectColumn(el))}
                                                            >
                                                                <Typography
                                                                    ml={1}
                                                                    sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                                                >
                                                                    { `• ${el.name} / ${t(el.type)}` }
                                                                </Typography>
                                                            </Grid>
                                                        )
                                                    })
                                                }
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid> }
                        </Grid> 
                    </DialogContent>
                    <Divider />
                    { checkPredictionStatus() ? 
                    <Grid item p={2}>
                        <Box sx={{ width: '100%' }}>
                            <LinearProgress />
                        </Box>
                    </Grid>
                    : 
                    <DialogActions>
                        <Button className="button-text-color" sx={{ marginRight: '10px' }} onClick={handleClose}>{t('CANCEL_BUTTON')}</Button>
                        <Button size="small" variant='contained' className="button-color" type="submit">{t('RUN_BUTTON')}</Button>
                    </DialogActions> }
                </form>
            </Dialog>
        </React.Fragment>
    )
}

export default NewPredictionDialog;
